import React, { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Alert, Button, Form, FormFeedback, Input, Label, Spinner } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAuth } from "auth/useAuth";
import { translateAuthError } from "auth/cognitoClient";
import { track, EVENTS } from "telemetry/track";
import AuthLayout from "./AuthLayout";

const ForgotPassword = () => {
  document.title = "Recuperar senha | ACIG Guapiaçu Cidadão";

  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitError, setSubmitError] = useState(null);

  const notice = location.state?.notice || null; // e.g. forced reset coming from /login

  const form = useFormik({
    enableReinitialize: true,
    initialValues: { email: location.state?.email || "" },
    validationSchema: Yup.object({
      email: Yup.string().trim().email("Informe um e-mail válido.").required("Informe seu e-mail."),
    }),
    onSubmit: async ({ email }) => {
      setSubmitError(null);
      try {
        const { email: sentTo } = await auth.forgotPassword(email);
        track(EVENTS.RESET_REQUESTED);
        navigate("/redefinir-senha", { state: { email: sentTo, justRequested: true } });
      } catch (err) {
        console.error("Forgot password failed:", err);
        if (err?.code === "UserNotFoundException" || err?.code === "NotAuthorizedException") {
          // Don't reveal whether the e-mail exists — behave as if the code was sent.
          navigate("/redefinir-senha", { state: { email: email.trim().toLowerCase(), justRequested: true } });
          return;
        }
        if (err?.code === "InvalidParameterException" && /verified/i.test(err?.message || "")) {
          setSubmitError("Este e-mail ainda não foi confirmado, por isso não podemos enviar o código. Confirme sua conta primeiro.");
          return;
        }
        setSubmitError(translateAuthError(err));
      }
    },
  });

  if (!auth.isLoading && auth.isAuthenticated) return <Navigate to="/" replace />;

  return (
    <AuthLayout
      title="Esqueceu a senha?"
      subtitle="Sem problema. Vamos enviar um código para você criar uma nova."
      footer={
        <p className="mb-0">
          Lembrou a senha?{" "}
          <Link to="/login" state={{ email: form.values.email }} className="fw-medium text-primary">Voltar para entrar</Link>
        </p>
      }
    >
      <Form onSubmit={form.handleSubmit} noValidate>
        {notice && <Alert color="warning">{notice}</Alert>}
        {submitError && <Alert color="danger">{submitError}</Alert>}

        <div className="mb-3">
          <Label htmlFor="fp-email">E-mail da sua conta</Label>
          <Input id="fp-email" name="email" type="email" inputMode="email" autoComplete="username"
            placeholder="voce@exemplo.com.br" autoFocus
            value={form.values.email} onChange={form.handleChange} onBlur={form.handleBlur}
            invalid={!!(form.touched.email && form.errors.email)} />
          <FormFeedback>{form.errors.email}</FormFeedback>
        </div>

        <div className="d-grid">
          <Button color="primary" type="submit" disabled={form.isSubmitting}>
            {form.isSubmitting && <Spinner size="sm" className="me-2" />}
            Enviar código
          </Button>
        </div>

        <div className="mt-3 text-center small text-muted">
          Já tem um código?{" "}
          <Link to="/redefinir-senha" state={{ email: form.values.email }}>Redefinir senha</Link>
        </div>
      </Form>
    </AuthLayout>
  );
};

export default ForgotPassword;
